// Considered: `ChatMessageCardAtom` (equal-weight chat rendering) — rejected. Chat sits next to
// consequence, not above it (README §1e): the Stream's chat cluster is a visually SUBORDINATE
// "quiet" card, not a full message thread. This composes `TextareaAtom` for the reply composer
// and reuses `useConversation`'s `sendMessage` rather than reimplementing posting.
// See spec Reuse Audit (specs/stream-trajectory-tools.md).
//
// Source: design/metaspace-tools/metaspace-tools-v2.jsx:230-274.

import React, { useState } from 'react';
import { useTranslation } from 'localization';
import type { ConversationMessage } from '../../../context/ToolbarContext';
import InitialsAvatarAtom from '../../atoms/InitialsAvatar/InitialsAvatarAtom';
import TextareaAtom from '../../atoms/Textarea/TextareaAtom';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import InspectableMolecule from '../Inspectable/InspectableMolecule';
import type { InspectorPayload } from 'types/inspector';
import './StreamChatClusterMolecule.scss';

export interface StreamChatClusterMoleculeProps {
  messages: ConversationMessage[];
  threadPayload: InspectorPayload;
  onInspect: (payload: InspectorPayload) => void;
  onReply: (content: string) => Promise<void> | void;
  onOpenComments: () => void;
  className?: string;
}

const StreamChatClusterMolecule: React.FC<StreamChatClusterMoleculeProps> = ({
  messages,
  threadPayload,
  onInspect,
  onReply,
  onOpenComments,
  className,
}) => {
  const { t } = useTranslation();
  const [earlier, setEarlier] = useState(false);
  const [replying, setReplying] = useState(false);
  const [draft, setDraft] = useState('');

  if (messages.length === 0) return null;

  const lead = messages[messages.length - 1];
  const earlierMessages = messages.slice(0, -1);

  const handleSend = async () => {
    const content = draft.trim();
    if (!content) return;
    await onReply(content);
    setDraft('');
    setReplying(false);
  };

  return (
    <div className={['stream-chat-cluster-molecule', className].filter(Boolean).join(' ')} data-testid="stream-chat-cluster">
      <InspectableMolecule payload={threadPayload} onInspect={onInspect} block>
        <div className="stream-chat-cluster-molecule__header">
          <InitialsAvatarAtom name={lead.author_name ?? t('stream.unknownActor')} />
          <span className="stream-chat-cluster-molecule__author">{lead.author_name ?? t('stream.unknownActor')}</span>
          <span className="stream-chat-cluster-molecule__meta">
            {t('toolInspector.thread.messageCount', { count: messages.length, defaultValue: `${messages.length} messages` })}
          </span>
        </div>
      </InspectableMolecule>

      {earlier && earlierMessages.length > 0 && (
        <div className="stream-chat-cluster-molecule__earlier">
          {earlierMessages.map((m) => (
            <div key={m.message_id} className="stream-chat-cluster-molecule__earlier-message">
              {m.content}
            </div>
          ))}
        </div>
      )}

      <div className="stream-chat-cluster-molecule__latest">{lead.content}</div>

      {replying ? (
        <div className="stream-chat-cluster-molecule__composer">
          <TextareaAtom
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={t('stream.panel.replyPlaceholder')}
            rows={2}
          />
          <div className="stream-chat-cluster-molecule__composer-row">
            <span className="stream-chat-cluster-molecule__hint">{t('stream.panel.repliesHint')}</span>
            <span className="stream-chat-cluster-molecule__composer-actions">
              <ButtonAtom shape="pill" size="small" variant="secondary" label={t('stream.acts.cancel')} onClick={() => { setReplying(false); setDraft(''); }} />
              <ButtonAtom shape="pill" size="small" variant="primary" icon="lucide:send" label={t('stream.acts.send')} onClick={() => void handleSend()} />
            </span>
          </div>
        </div>
      ) : (
        <div className="stream-chat-cluster-molecule__footer">
          {earlierMessages.length > 0 && (
            <button type="button" className="stream-chat-cluster-molecule__earlier-toggle" onClick={() => setEarlier((e) => !e)}>
              {earlier
                ? t('stream.acts.hideEarlier')
                : t('stream.acts.showEarlier', { count: earlierMessages.length })}
            </button>
          )}
          <span className="stream-chat-cluster-molecule__footer-actions">
            <ButtonAtom shape="pill" size="small" variant="secondary" icon="lucide:arrow-up-right" label={t('stream.acts.openInComments')} onClick={onOpenComments} />
            <ButtonAtom shape="pill" size="small" variant="primary" icon="lucide:reply" label={t('stream.acts.reply')} onClick={() => setReplying(true)} />
          </span>
        </div>
      )}
    </div>
  );
};

StreamChatClusterMolecule.displayName = 'StreamChatClusterMolecule';

export default StreamChatClusterMolecule;
